import { z } from "zod";

/**
 * Shared DTO cho Admin AI audit-trace + safety board (US-AE8.F4-0028 · @gate ai+pii):
 *  - @UC9073 ai-audit-trace  — GET /admin/ai/audit-trace (list: prompt → intent → amount →
 *    idempotencyKey → ledger_transaction; read-only join ledger).
 *  - @UC9075 ai-safety-board — GET /admin/ai/safety-events (list ca an toàn bị chặn + filter kind).
 *
 * Một nguồn sự thật shape cho api (S01) + admin web (S03) + e2e (S04) — tránh drift (DRY).
 *
 * BẢO MẬT / BẤT BIẾN (invariants):
 * - `rawPrompt` REDACTION-AWARE: nullable — chỉ SUPER_ADMIN thấy thô; vai khác server trả null
 *   (DR-2). Contract KHÔNG ép lộ PII; enforce redaction ở S01 (server).
 * - `proposalAmountMinor` = BigInt minor VND → STRING trên wire (money no-float). TỪ fact-block
 *   (API), KHÔNG do LLM tính (AI-INV-2). null cho intent read-only.
 * - `ai_audit_log` + ledger APPEND-ONLY (AI-INV-7 + G2) — chỉ có endpoint READ; KHÔNG shape mutate.
 * - Safety `kind` taxonomy đóng: injection | tool_scope | fact_block | escalation (DR-3).
 * - `.strict()` — KHÔNG khai báo field lạ; reject tại biên (chống leak).
 * - Cursor opaque string — đối xứng common/pagination/cursor.
 */

/** Max page-size cho list audit-trace + safety. Default (20) do backend áp khi vắng. */
export const ADMIN_AI_AUDIT_MAX_LIMIT = 100;
export const ADMIN_AI_SAFETY_MAX_LIMIT = 100;

// ── audit-trace ──────────────────────────────────────────────────────────────────────────────

/** Kết cục một tương tác chat AI. */
export const aiAuditOutcomeSchema = z.enum(["executed", "proposed", "clarify", "blocked"]);
export type AiAuditOutcome = z.infer<typeof aiAuditOutcomeSchema>;

/**
 * Một dòng audit-trace: chuỗi prompt(redacted) → intent → proposal(amount fact-block) →
 * idempotencyKey → ledger_transaction (join read-only). rawPrompt nullable (redaction-aware).
 */
export const aiAuditTraceItemSchema = z
  .object({
    auditId: z.string(),
    conversationId: z.string().nullable(),
    /** end-user gây tương tác (id — surface hiển thị ref/chip; null nếu không truy). */
    actorUserId: z.string().nullable(),
    /**
     * Display-name actor resolve server-side (T01 · @UC9135): username(social)→email→phone. null khi
     * actorUserId null / không resolve được → UI fallback uuid-slice. KHÔNG phơi PII ngoài name.
     */
    actorName: z.string().nullable(),
    groupId: z.string().nullable(),
    fundId: z.string().nullable(),
    /** câu chat thô — null cho vai KHÔNG đủ (redaction; chỉ SUPER_ADMIN thấy thô — DR-2). */
    rawPrompt: z.string().nullable(),
    /** cờ đã che (true ⇒ rawPrompt=null do redaction, KHÔNG phải vắng data). */
    promptRedacted: z.boolean(),
    detectedIntent: z.string(),
    proposalKind: z.string().nullable(),
    /** minor VND (fact-block API, no-float) → string; null cho intent read-only. */
    proposalAmountMinor: z.string().nullable(),
    idempotencyKey: z.string().nullable(),
    /** id bút toán ledger đã join qua idempotencyKey (read-only); null nếu không nối được. */
    ledgerTxId: z.string().nullable(),
    outcome: aiAuditOutcomeSchema,
    toolTier: z.string().nullable(),
    createdAt: z.string(),
  })
  .strict();
export type AiAuditTraceItem = z.infer<typeof aiAuditTraceItemSchema>;

/** Envelope list audit-trace (cursor pagination) — đối xứng repo {data, pageInfo}. */
export const aiAuditTraceListResponseSchema = z
  .object({
    data: z.array(aiAuditTraceItemSchema),
    pageInfo: z
      .object({
        hasNext: z.boolean(),
        nextCursor: z.string().nullable(),
      })
      .strict(),
  })
  .strict();
export type AiAuditTraceListResponse = z.infer<typeof aiAuditTraceListResponseSchema>;

/**
 * Detail read-only (US-AE-IMP2-T11-S6 · @UC9156) — GET /admin/ai/audit-trace/:id. Truy vết chi tiết
 * 1 mục: prompt(redacted) → intent → amount → idempotencyKey → ledgerTxId — CÙNG shape list item
 * (READ-ONLY). rawPrompt redaction-aware (chỉ SUPER_ADMIN thô). `.strict()` reject field lạ.
 * KHÔNG mở path mutate (`ai_audit_log` + ledger APPEND-ONLY — AI-INV-7 + G2).
 */
export const aiAuditTraceDetailResponseSchema = aiAuditTraceItemSchema;
export type AiAuditTraceDetailResponse = z.infer<typeof aiAuditTraceDetailResponseSchema>;

// ── safety-event ─────────────────────────────────────────────────────────────────────────────

/** Taxonomy loại vi phạm an toàn (đóng — DR-3). */
export const aiSafetyKindSchema = z.enum(["injection", "tool_scope", "fact_block", "escalation"]);
export type AiSafetyKind = z.infer<typeof aiSafetyKindSchema>;

/** Một dòng safety event trong board (ca bị chặn). detail đã redacted-at-write (no-PII thô). */
export const aiSafetyEventItemSchema = z
  .object({
    eventId: z.string(),
    conversationId: z.string().nullable(),
    auditId: z.string().nullable(),
    actorUserId: z.string().nullable(),
    /** Display-name actor resolve server-side (T01 · @UC9135). null → UI fallback uuid-slice. */
    actorName: z.string().nullable(),
    groupId: z.string().nullable(),
    kind: aiSafetyKindSchema,
    /** mô tả loại vi phạm (đã redacted no-PII). */
    detail: z.string(),
    blocked: z.boolean(),
    createdAt: z.string(),
  })
  .strict();
export type AiSafetyEventItem = z.infer<typeof aiSafetyEventItemSchema>;

/** Envelope list safety events (cursor pagination). */
export const aiSafetyEventListResponseSchema = z
  .object({
    data: z.array(aiSafetyEventItemSchema),
    pageInfo: z
      .object({
        hasNext: z.boolean(),
        nextCursor: z.string().nullable(),
      })
      .strict(),
  })
  .strict();
export type AiSafetyEventListResponse = z.infer<typeof aiSafetyEventListResponseSchema>;
